import React, { useState, useRef, useEffect } from "react";
import { useCurrency } from "../context/CurrencyContext";

export default function CurrencySelector() {
  const { selectedCurrency, setSelectedCurrency, currencies, rates, lastUpdated, isFallback } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentInfo = rates[selectedCurrency] || {
    flag: "🇮🇳",
    symbol: "₹",
    code: "INR",
    name: "Indian Rupee"
  };

  const filteredCurrencies = currencies.filter(
    (c) =>
      c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="currency-selector-wrapper" ref={dropdownRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="currency-trigger-btn"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px 12px",
          background: "var(--bg-secondary, #ffffff)",
          border: "1px solid var(--border-color, #e2e8f0)",
          borderRadius: "20px",
          color: "var(--text-primary, #0f172a)",
          fontSize: "12.5px",
          fontWeight: "700",
          cursor: "pointer",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          transition: "all 0.2s ease"
        }}
        title={`Change currency (Current: ${selectedCurrency})`}
      >
        <span style={{ fontSize: "14px" }}>🌍</span>
        <span style={{ fontSize: "14px" }}>{currentInfo.flag}</span>
        <span style={{ color: "#FD6101", fontWeight: "800" }}>{currentInfo.symbol}</span>
        <span>{selectedCurrency}</span>
        <span style={{ fontSize: "9px", opacity: 0.7 }}>▼</span>
      </button>

      {isOpen && (
        <div
          className="currency-dropdown-menu"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: "280px",
            maxHeight: "360px",
            background: "var(--bg-secondary, #ffffff)",
            border: "1px solid var(--border-color, #e2e8f0)",
            borderRadius: "14px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {/* Header & Search Bar */}
          <div style={{ padding: "10px", borderBottom: "1px solid var(--border-color, #f1f5f9)", background: "var(--bg-tertiary, #f8fafc)" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--text-secondary, #64748b)", textTransform: "uppercase", marginBottom: "6px" }}>
              Select Display Currency
            </div>
            <input
              type="text"
              placeholder="Search currency or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "6px 10px",
                border: "1px solid var(--border-color, #cbd5e1)",
                borderRadius: "8px",
                fontSize: "12px",
                outline: "none",
                background: "var(--bg-secondary, #ffffff)",
                color: "var(--text-primary, #0f172a)",
                boxSizing: "border-box"
              }}
              autoFocus
            />
          </div>

          {/* Fallback Warning Notice if Live API fails */}
          {isFallback && (
            <div style={{ padding: "6px 10px", background: "#fffbe8", borderBottom: "1px solid #fef3c7", fontSize: "10.5px", color: "#b45309" }}>
              ⚠️ Live exchange rates temporarily unavailable. Using cached rates.
            </div>
          )}

          {/* List of Currencies */}
          <div style={{ flex: 1, overflowY: "auto", padding: "4px" }}>
            {filteredCurrencies.map((c) => {
              const isSelected = c.code === selectedCurrency;
              return (
                <button
                  key={c.code}
                  onClick={() => {
                    setSelectedCurrency(c.code);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 10px",
                    border: "none",
                    borderRadius: "8px",
                    background: isSelected ? "rgba(124, 58, 237, 0.1)" : "transparent",
                    color: isSelected ? "#FD6101" : "var(--text-primary, #0f172a)",
                    fontSize: "12.5px",
                    fontWeight: isSelected ? "800" : "500",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s ease"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "16px" }}>{c.flag}</span>
                    <div>
                      <div style={{ lineHeight: "1.2" }}>
                        <span style={{ fontWeight: "700" }}>{c.code}</span> ({c.symbol})
                      </div>
                      <div style={{ fontSize: "10px", color: "var(--text-secondary, #64748b)" }}>{c.name}</div>
                    </div>
                  </div>
                  {isSelected && <span style={{ color: "#FD6101", fontWeight: "bold" }}>✓</span>}
                </button>
              );
            })}
            {filteredCurrencies.length === 0 && (
              <div style={{ padding: "16px", textAlign: "center", fontSize: "12px", color: "var(--text-secondary, #64748b)" }}>
                No currency matches "{searchTerm}"
              </div>
            )}
          </div>

          {/* Footer Timestamp */}
          <div style={{ padding: "8px 10px", borderTop: "1px solid var(--border-color, #f1f5f9)", background: "var(--bg-tertiary, #f8fafc)", fontSize: "10px", color: "var(--text-secondary, #64748b)", display: "flex", justifyContent: "space-between" }}>
            <span>Base: 🇮🇳 INR (₹)</span>
            <span>Updated: {lastUpdated ? lastUpdated.split(" ")[0] : "Today"}</span>
          </div>
        </div>
      )}
    </div>
  );
}

